import { useCallback, useMemo, useState } from "react";
import "../../css/CreateEvent.css";
import {
  CustomTicketV2,
  Event,
  TicketType,
  NotificationType,
  ThemeType,
} from "@markit/common.types";
import { LightTheme, useTheme } from "../../hooks/useTheme";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import CreateEventSectionHeader from "./CreateEventSectionHeader";
import {
  getTotalTicketsDistributedInGroup,
  getTotalTicketsSoldInGroup,
} from "../../utils/eventUtils/eventUtils";
import ConfirmDeleteModal from "../Containers/ConfirmPopups/ConfirmDeleteModal";
import {
  accountActions,
  getAccountState,
} from "../../redux/slices/accountSlice";
import { useSelector, useDispatch } from "react-redux";
import { showNotificationBanner } from "../../utils/notificationUtils";
import RectangleButton from "../Buttons/RectangleButton";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import StripeConnectLabel from "../Stripe/StripeConnectLabel";
import DragDropList from "../Lists/DragDropList";
import DragDropListIcon from "../Lists/DragDropListIcon";
import CreateEventCustomTicketOption from "./CreateEventCustomTicketOption";
import ExpandableBox from "../Containers/ExpandableBox";
import {
  getRequestTicketLabel,
  isProductSpecificType,
  productItemLabel,
} from "@markit/common.utils";
import AlertContainer from "../Containers/AlertContainer";

interface CreateEventTicketsProps {
  eventSettings: Event;
  updateEventSettings: (update: Partial<Event>) => void;
  showConnectStripeModal: () => void;
  readyForPayment: boolean;
  hardTheme: ThemeType;
  noHeader?: boolean;
}

const CreateEventTickets = (props: CreateEventTicketsProps) => {
  const {
    eventSettings,
    updateEventSettings,
    showConnectStripeModal,
    readyForPayment,
    hardTheme,
    noHeader,
  } = props;

  const { currentEventFormQuestions } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const { theme } = useTheme(hardTheme);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDeleteTicketModalOpen, setIsDeleteTicketModalOpen] = useState(false);
  const [ticketIdToDelete, setTicketIdToDelete] = useState("");
  const [alertText, setAlertText] = useState({ header: "", subHeader: "" });

  const [isTicketsModalOpen, setIsTicketsModalOpen] = useState(false);
  const [currTicketNumber, setCurrTicketNumber] = useState(-1);

  const styles = {
    placeholder: {
      fontSize: 12,
      color: Colors.GRAY2,
    },
  };

  const itemLabel = useMemo(
    () => productItemLabel(eventSettings.eventType),
    [eventSettings.eventType]
  );

  const itemType = useCallback(
    (capitalize: boolean) =>
      capitalize
        ? itemLabel.singularCap + " " + itemLabel.kindCap
        : itemLabel.singular + " " + itemLabel.kind,
    [itemLabel]
  );

  const deleteTicketOption = useCallback(
    async (ticket: CustomTicketV2) => {
      // check if the user can delete the specified ticket option
      const [totalTicketsSoldInGroup, totalTicketsDistributedInGroup] =
        await Promise.all([
          getTotalTicketsSoldInGroup(
            eventSettings.id,
            ticket.id,
            eventSettings.createdBy
          ),
          getTotalTicketsDistributedInGroup(eventSettings.id, ticket.id),
        ]);
      if (totalTicketsSoldInGroup + totalTicketsDistributedInGroup === 0) {
        if (eventSettings.customTickets.length === 1) {
          setAlertText({
            header: `You must have at least one ${itemType(false)}`,
            subHeader: "",
          });
          return;
        }
        setTicketIdToDelete(ticket.id);
        setIsDeleteTicketModalOpen(true);
      } else {
        setAlertText({
          header: `This ${itemType(
            false
          )} cannot be deleted since one or more have been sold or distributed.`,
          subHeader: `You can mark this ${itemType(
            false
          )} as hidden instead so it won't show up on checkout.`,
        });
      }
    },
    [
      eventSettings.id,
      eventSettings.createdBy,
      eventSettings.customTickets.length,
      itemType,
    ]
  );

  const resetDeleteTicketModal = useCallback(() => {
    setTicketIdToDelete("");
    setIsDeleteTicketModalOpen(false);
  }, []);

  const finalizeDeleteTicket = useCallback(() => {
    const newCustomTickets = eventSettings.customTickets.filter(
      (tix) => tix.id !== ticketIdToDelete
    );
    updateEventSettings({
      customTickets: newCustomTickets,
    });

    // This section removes any event form questions that are only used by the ticket being deleted.
    // This is important because we don't want any form questions that aren't used by any ticket types.
    const otherQuestionIds = eventSettings.customTickets
      .filter((ticket) => ticket.id !== ticketIdToDelete)
      .flatMap((ticket) => ticket.formQuestions);

    const updatedFormQuestions = currentEventFormQuestions.filter((question) =>
      otherQuestionIds.includes(question.id)
    );

    dispatch(
      accountActions.reorderCurrentEventFormQuestions(updatedFormQuestions)
    );

    resetDeleteTicketModal();
    showNotificationBanner(
      dispatch,
      `${itemType(true)} Deleted`,
      NotificationType.NEGATIVE
    );
  }, [
    currentEventFormQuestions,
    dispatch,
    eventSettings.customTickets,
    itemType,
    resetDeleteTicketModal,
    ticketIdToDelete,
    updateEventSettings,
  ]);

  const handleTicketsOnDrag = useCallback(
    (newCustomTickets: CustomTicketV2[]) => {
      if (!newCustomTickets.length) return;
      updateEventSettings({
        ...eventSettings,
        customTickets: newCustomTickets,
      });
    },
    [eventSettings, updateEventSettings]
  );

  const renderTicketPreviews = (
    ticket: CustomTicketV2,
    index: number,
    dragHandleProps: any
  ): JSX.Element => {
    return (
      <div key={index} className="onboarding-sell-products-pricing-target">
        <div
          style={{
            paddingBlock: 14,
            borderRadius: 8,
            border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
            ...theme.NewPrimaryBG,
          }}
        >
          <div className="AlignedRowSpaced" style={{ paddingInline: 14 }}>
            <div className="AlignedRow" style={{ gap: 14 }}>
              {eventSettings.customTickets.length > 1 ? (
                <DragDropListIcon dragHandleProps={dragHandleProps} />
              ) : null}
              <div className="ColumnNormal" style={{ gap: 4 }}>
                <span className="bodyMedium" style={{ ...theme.PrimaryText }}>
                  {ticket.label}
                </span>
                <span style={styles.placeholder}>
                  {ticket.type === TicketType.PAID
                    ? `$${ticket.price / 100}${
                        ticket.flexiblePricing
                          ? `± (min $${ticket.minPrice / 100})`
                          : ""
                      }`
                    : ticket.type === TicketType.FREE
                    ? "Free"
                    : getRequestTicketLabel(eventSettings.eventType)}
                </span>
                {ticket.type === TicketType.PAID && !readyForPayment ? (
                  <StripeConnectLabel
                    showConnectStripeModal={showConnectStripeModal}
                  />
                ) : null}
              </div>
            </div>
            <div className="AlignedRow" style={{ gap: 7 }}>
              <div>
                <Icon
                  className="TrashButton"
                  icon={"ion:trash"}
                  height={20}
                  onClick={() => deleteTicketOption(ticket)}
                />
              </div>
              <div>
                <Icon
                  className={
                    "EditButton " +
                    (theme === LightTheme
                      ? "EditButtonLight"
                      : "EditButtonDark")
                  }
                  icon={"ion:create"}
                  height={20}
                  onClick={() => {
                    setCurrTicketNumber(index);
                    setIsTicketsModalOpen(true);
                  }}
                />
              </div>
            </div>
          </div>
          <HorizontalDivider
            altMargin={10}
            altWidth={1}
            eventTheme={hardTheme}
          />
          <div
            className="AlignedRowSpaced"
            style={{ paddingInline: 14, gap: 14, alignItems: "flex-start" }}
          >
            <div className="ColumnNormal" style={{ gap: 4 }}>
              {ticket.hideTicket ? (
                <div className="AlignedRow" style={{ gap: 7 }}>
                  <Icon
                    icon="ion:eye-off"
                    height={14}
                    style={{ color: Colors.ORANGE1 }}
                  />
                  <span
                    style={{ ...styles.placeholder, color: Colors.ORANGE1 }}
                  >
                    Hidden {itemType(true)}
                  </span>
                </div>
              ) : null}
              {ticket.description ? (
                <span style={styles.placeholder}>{ticket.description}</span>
              ) : null}
              <span style={{ ...styles.placeholder }}>
                Min {ticket.minQuantity}, Max {ticket.maxQuantity}
              </span>
              {ticket.quantityAvailable > 0 ? (
                <span style={{ ...styles.placeholder }}>
                  {ticket.quantityAvailable} Available
                </span>
              ) : null}
              {ticket.showSpotsLeft ? (
                <span style={{ ...styles.placeholder }}>
                  Showing{" "}
                  {isProductSpecificType(eventSettings.eventType)
                    ? "left in stock"
                    : "spots left"}
                </span>
              ) : null}
            </div>
            {ticket.mediaURL ? (
              <img
                src={ticket.mediaURL}
                alt="Ticket Image"
                style={{
                  width: 70,
                  borderRadius: 8,
                  border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
                }}
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {!noHeader ? (
        <CreateEventSectionHeader
          title="Ticketing"
          isMinimized={isMinimized}
          setIsMinimized={setIsMinimized}
          theme={theme}
        />
      ) : null}
      <ExpandableBox>
        {!isMinimized ? (
          <div
            className="ColumnNormal"
            style={{ gap: 10, paddingTop: noHeader ? 0 : 14 }}
          >
            <DragDropList
              items={eventSettings.customTickets}
              handleChange={handleTicketsOnDrag}
            >
              {(
                ticket: CustomTicketV2,
                index: number,
                dragHandleProps: any
              ) => {
                return renderTicketPreviews(ticket, index, dragHandleProps);
              }}
            </DragDropList>
            <RectangleButton
              buttonLabel={
                <span style={{ fontWeight: 500 }}>New {itemType(true)}</span>
              }
              onPress={() => {
                setCurrTicketNumber(-1);
                setIsTicketsModalOpen(true);
              }}
              altColor={theme.NewPrimaryBG.backgroundColor}
              altTextColor={theme.PrimaryText.color}
              iconRight={
                <Icon
                  icon="ion:add-circle"
                  height={18}
                  style={theme.PrimaryText}
                />
              }
              containerStyles={{
                paddingInline: 30,
                paddingBlock: 14,
                border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
              }}
            />
          </div>
        ) : null}
      </ExpandableBox>
      <ConfirmDeleteModal
        heading={`Are you sure you want to delete this ${itemType(false)}?`}
        subtext="You cannot undo this."
        deleteButtonText={"Delete"}
        hideModal={!isDeleteTicketModalOpen}
        setIsVisible={(open: boolean) => {
          setTicketIdToDelete("");
          setIsDeleteTicketModalOpen(open);
        }}
        deleteOnPress={finalizeDeleteTicket}
        theme={theme}
      />
      <AlertContainer
        headerComp={alertText.header}
        subHeaderComp={alertText.subHeader}
        closeModal={() => setAlertText({ header: "", subHeader: "" })}
        hideModal={alertText.header === ""}
        theme={theme}
      />
      {/* Modal for setting tickets */}
      {isTicketsModalOpen ? (
        <CreateEventCustomTicketOption
          eventSettings={eventSettings}
          updateEventSettings={updateEventSettings}
          ticketOption={currTicketNumber}
          setIsVisible={setIsTicketsModalOpen}
          hardTheme={hardTheme}
        />
      ) : null}
    </div>
  );
};

export default CreateEventTickets;
